import { Typography } from 'antd';
import React from 'react';
import { log } from './LogStreamTable';
import Paragraph from 'antd/es/typography/Paragraph';
import JsonDisplay from './JsonDisplay';
import { Channels } from '../common/channelsEnum';

const { Text } = Typography;

const FailureDetails = ({ log }: { log: log }): JSX.Element => {
  if (log.message.message === 'Incorrect notificationId') {
    return (
      <>
        <Paragraph>
          There are no notifications with notificationId:
          <Text code>{log.message.notificationId}</Text>
        </Paragraph>
        <Text>
          Go to the notifications page on the left, to see the list of your
          configured notifications and their IDs.
        </Text>
      </>
    );
  }

  // OnAccountSuppressionList bounce
  if (
    log.message.message &&
    typeof log.message.message !== 'string' &&
    log.message.message.bounce?.bounceType === 'Permanent' &&
    log.message.message.bounce?.bounceSubType === 'OnAccountSuppressionList'
  ) {
    return (
      <>
        <Paragraph type="danger">
          {log.message.message.bounce.bouncedRecipients[0].emailAddress}
        </Paragraph>
        <Paragraph type="danger">
          Email suppressed due to being on our suppression list.
        </Paragraph>
        <Paragraph>
          In a previous attempt when sending emails to this address, the email
          was bounced, meaning that the address does not exist or the mail
          server is not accepting the email due to suspecting it to be spam.
        </Paragraph>
        <Paragraph>
          Therefore, we have added this address to our suppression list and will
          no longer send email notifications to this address. This is to protect
          your email reputation.
        </Paragraph>
        <Paragraph>
          If you believe this is a mistake, please contact support to remove
          this address from suppression.
        </Paragraph>
      </>
    );
  }

  // Other bounces
  if (
    log.message.message &&
    typeof log.message.message !== 'string' &&
    log.message.message.bounce?.bounceType === 'Permanent'
  ) {
    return (
      <>
        <Paragraph type="danger">Hard bounce:</Paragraph>

        {log.message.message.bounce?.bounceSubType === 'General' && (
          <Paragraph>
            The recipient's email provider gave us a permanent bounce response,
            meaning that the address does not exist or the mail server is not
            accepting the email due to suspecting it to be spam.
          </Paragraph>
        )}

        {log.message.message.bounce?.bounceSubType === 'Suppressed' && (
          <Paragraph>
            The recipient's email address is on our global suppression list
            because it has a recent history of producing hard bounces.
          </Paragraph>
        )}

        <Paragraph>
          If you need more information or believe this is a mistake, please
          contact support.
        </Paragraph>

        <Text type="secondary">Bounce Event:</Text>
        <JsonDisplay json={log.message.message} />
      </>
    );
  }

  // Transient bounce
  if (
    log.message.message &&
    typeof log.message.message !== 'string' &&
    log.message.message.bounce?.bounceType === 'Transient'
  ) {
    return (
      <>
        <Paragraph type="danger">Temporary Bounce</Paragraph>
        {log.message.message.bounce?.bounceSubType === 'General' && (
          <Paragraph>
            The recipient's email provider gave us a temporary bounce response.
            This typically happens when the recipient has set up an "out of the
            office" automatic reply. This type of soft bounce generally fixes
            itself. If the bounce was a result of an "out of the office" reply,
            then the soft bounce resolves itself after the automatic reply is
            removed by the recipient.
          </Paragraph>
        )}

        {log.message.message.bounce?.bounceSubType === 'MailboxFull' && (
          <Paragraph>
            The recipient mailbox is full and can't accept any more emails. You
            might be able to send emails to the same recipient later when their
            mailbox is no longer full.
          </Paragraph>
        )}

        {log.message.message.bounce?.bounceSubType === 'MessageTooLarge' && (
          <Paragraph>
            The recipient mail server can't accept the email because the file
            size of the message is too large. You can try to resolve this type
            of bounce by reducing your message size.{' '}
          </Paragraph>
        )}

        {log.message.message.bounce?.bounceSubType === 'ContentRejected' && (
          <Paragraph>
            The recipient mail server doesn't allow the content of the email.
            You can try to resolve this type of bounce by modifying the content
            of your email.
          </Paragraph>
        )}

        {log.message.message.bounce?.bounceSubType === 'AttachmentRejected' && (
          <Paragraph>
            The email contains an unacceptable attachment. Some email providers
            block emails with attachments of a certain file type or large
            attachments. After you remove or modify your email attachments, you
            can try to send the email to this recipient again.
          </Paragraph>
        )}

        <Text type="secondary">Bounce Event:</Text>
        <JsonDisplay json={log.message.message} />
      </>
    );
  }

  // OnAccountSuppressionList complaint
  if (
    log.message.message &&
    typeof log.message.message !== 'string' &&
    log.message.message.complaint?.complaintSubType ===
      'OnAccountSuppressionList'
  ) {
    return (
      <>
        <Paragraph type="danger">
          {log.message.message.complaint.complainedRecipients[0].emailAddress}
        </Paragraph>
        <Paragraph type="danger">
          Email suppressed due to reporting a complaint:
        </Paragraph>
        <Paragraph>
          In a previous attempt when sending emails to this address, the
          recipient has reported a complaint against your email, for example by
          marking it as spam.
        </Paragraph>
        <Paragraph>
          Therefore, we have added this address to our suppression list and will
          no longer send email notifications to this address. This is to protect
          your email reputation.
        </Paragraph>
        <Paragraph>
          If you believe this is a mistake, please contact support to remove
          this address from suppression.
        </Paragraph>
      </>
    );
  }

  // SMS / Call
  if (
    (log.message.SmsStatus || log.message.CallStatus) &&
    log.message.ErrorCode
  ) {
    if (
      (log.message.ErrorCode === '30005' ||
        log.message.ErrorCode === '30006') &&
      log.message.To?.startsWith('+1500555')
    ) {
      return (
        <>
          <Paragraph type="danger">Wrong number:</Paragraph>
          <Paragraph>
            You are trying to send SMS to our test numbers. Please replace the
            test number ({log.message.To}) with a valid phone number.
          </Paragraph>
        </>
      );
    }

    if (log.message.ErrorCode === '30005') {
      return (
        <>
          <Paragraph type="danger">Wrong number:</Paragraph>
          <Paragraph>
            The destination number you are trying to reach is unknown and may no
            longer exist.
          </Paragraph>

          <Text type="secondary">Error Details:</Text>
          <JsonDisplay json={log.message} />
        </>
      );
    }

    if (log.message.ErrorCode === '13224') {
      return (
        <>
          <Paragraph type="danger">Unsupported Number:</Paragraph>
          <Paragraph>
            We do not support calling this number or the number is invalid.
          </Paragraph>

          <Paragraph>
            This error occurs when the provided number is not in E.164 format;
            for example using local dialing pattern 01234567890 instead of the
            full E.164 address +441234567890. This can also occur when calls are
            attempted to non-existent country codes, area codes, or exchanges.
            For example, there is no +693 country code, no +1238 US area code,
            and no 555 US exchange.
          </Paragraph>

          <Paragraph>
            This can also occur with properly-formatted, valid destinations if
            none of the carrier partners are able to deliver the call; for
            example due to a telecom network outage in the phone number's local
            area.
          </Paragraph>

          <Paragraph>
            Use E.164 formatting and ensure the destination is a valid,
            in-service destination.
          </Paragraph>

          <Text type="secondary">Error Details:</Text>
          <JsonDisplay json={log.message} />
        </>
      );
    }

    if (log.message.ErrorCode === '30007') {
      return (
        <>
          <Paragraph type="danger">Message Filtered:</Paragraph>
          <Paragraph>
            Your message content was flagged as going against carrier
            guidelines. Your message was filtered (blocked) by us or by the
            carrier. This may be done for violating a our Policy or Acceptable
            Use Policy, or by a wireless carrier for violating carrier rules or
            regulations.
          </Paragraph>

          <Paragraph>
            Examples of messaging that would be blocked by us are spam,
            phishing, and fraud. This filtering system is in place to protect
            mobile subscribers from spam or other forms of malicious or unwanted
            messages.
          </Paragraph>

          <Paragraph>
            Wireless carriers have filtering systems in place as well. These
            carrier filters are used to block abusive traffic, as well as to
            enforce rules or regulations about what types of messaging are
            allowed to that country or mobile network.
          </Paragraph>
        </>
      );
    }

    if (log.message.ErrorCode === '30008') {
      return (
        <>
          <Paragraph type="danger">Unknown error:</Paragraph>
          <Paragraph>
            This is a result of receiving a very generic error from the telecom
            carriers. We have no further details about and we truly don't know
            what has caused this error.
          </Paragraph>

          <Paragraph>Possible causes:</Paragraph>

          <ul>
            <li>
              Check that the phone you were sending to is turned on and can
              receive SMS messages.
            </li>
            <li>
              Ensure that the phone is not roaming off network. We cannot
              guarantee message delivery on roaming phones.
            </li>
            <li>
              Try sending to other phones who have the same mobile carrier. If
              messages to other phones go through, the issue is likely device
              related. Try rebooting the device or contact the mobile carrier
              for help.
            </li>

            <li>
              Try sending a shorter message to the phone, with simple content
              that does not include any special characters. This would give our
              support team an idea as to whether the failure is related to
              concatenation or character encoding.
            </li>

            <li>
              NotificationAPI support can help investigate what went wrong with
              our carriers. Please open a support request and include a minimum
              of 3 or more message SIDs with the same error. Per our carriers'
              requirements, these SIDs can be no older than 48 hours at most.
            </li>
          </ul>
        </>
      );
    }

    if (log.message.ErrorCode === '30034') {
      return (
        <>
          <Paragraph type="danger">
            Message from an unregistered number:
          </Paragraph>
          <Paragraph>
            Messages sent to US numbers will not be delivered if they are sent
            from numbers that are not associated with an approved A2P 10DLC
            Campaign.
          </Paragraph>

          <Paragraph>
            NotificationAPI will help you attain your A2P 10DLC designation so
            you can start sending messages from this number. Please contact our
            support to get started.
          </Paragraph>
        </>
      );
    }
  }
  if (
    log.message.channels?.includes(Channels.EMAIL) &&
    log.message.error?.error.code === 'ECONNREFUSED' &&
    log.message.error?.error.errno === -111 &&
    log.message.error?.error.syscall === 'connect' &&
    log.message.error?.error.sourceUrl
  ) {
    return (
      <>
        <Paragraph type="danger">Invalid Email Attachment URL</Paragraph>
        <Paragraph>
          We could not fetch the following URL that you provided as an email
          attachment:
        </Paragraph>
        <Text code>{log.message.error?.error.sourceUrl}</Text>
      </>
    );
  }
  if (
    log.message.channels?.includes(Channels.SMS) &&
    log.message.error?.error.code === 21211
  ) {
    return (
      <>
        <Paragraph type="danger">Invalid Phone Number</Paragraph>
        <Paragraph>
          The phone number you supplied was not valid or was incorrectly
          formatted. NotificationAPI accepts phone numbers in the following
          format:
        </Paragraph>
        <Text code>
          [+] [country code] [subscriber number including area code]
        </Text>
        <Paragraph>
          This error will also occur if you attempted to make a call or send a
          message to a sender number that is assigned to your NotificationAPI
          account. For viewing and managing the sender phone numbers associated
          with your account, please visit this
          <a href="https://docs.notificationapi.com/channels/sms#sender-phone-numbers">
            link
          </a>
          .
        </Paragraph>
      </>
    );
  }

  // web push: unsubscribed/expired
  if (
    log.message.channels?.includes(Channels.WEB_PUSH) &&
    log.message.error?.error?.name === 'WebPushError' &&
    log.message.error?.error.body ===
      'push subscription has unsubscribed or expired.\n'
  ) {
    return (
      <>
        <Paragraph type="danger">
          Unsubscribed or expired push subscription
        </Paragraph>
      </>
    );
  }

  window.Rollbar?.error('Unknown log in logs page', log);
  return (
    <>
      <Text type="danger">
        Unknown failure. Our team has been automatically notified and they are
        looking into the issue. You may receive an email from us or check back
        on this log to see updated information.
      </Text>
    </>
  );
};
export default FailureDetails;
